import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Icon from 'components/Utils/NewIcon';
import UIUtils from 'services/UIUtils';
import SelectableListItem from 'components/Utils/SelectableListItem';
import './style.scss';
import DefaultReactSelect from 'components/Utils/DefaultReactSelect';
import StaffUtils, { StaffBadge } from 'services/StaffUtils';
import PerformanceUtils from 'services/PerformanceUtils';
import RouteUtils from 'services/RouteUtils';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { useNavStack } from 'careand-redux/slices/ui/hooks';
import { Stacks } from 'careand-redux/slices/ui/navigation';

const StaffSearchField = (props) => {
  const dispatch = useAppDispatch();

  const nav = useNavStack(Stacks.Master);

  const { onSelect } = props;

  const [results, setResults] = useState(props.prefill || []);
  const [typingTimeout, setTypingTimeout] = useState(undefined);
  const [search, setSearch] = useState();
  const [clickedStaff, setClickedStaff] = useState(props.staff);
  const [searching, setSearching] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (props.staff) setClickedStaff(props.staff);
  }, [props.staff]);

  useEffect(() => {
    if (onSelect) {
      onSelect(clickedStaff);
    }
  }, [clickedStaff]);

  function handleSearchChange(value) {
    clearTimeout(typingTimeout);
    setSearch(value);

    let timeoutHandle = setTimeout(async () => {
      if (value !== undefined && value !== '') await searchStaff(value);
    }, UIUtils.SEARCH_TYPE_TIMEOUT);

    setTypingTimeout(timeoutHandle);
  }

  async function searchStaff(value) {
    setSearching(true);

    let [adminsResult, physiciansResult] = await Promise.all([
      props.ignoreAdmins ? {} : dispatch(api.Admins.getAdmins({ search: value })),
      dispatch(api.Physicians.getPhysicians({ name: value })),
    ]);

    let physicians = [],
      admins = [];

    if (physiciansResult) physicians = PerformanceUtils.objectToArray(physiciansResult);

    if (adminsResult) admins = PerformanceUtils.objectToArray(adminsResult);

    let result = [...physicians, ...admins];

    result = result.map((staff) => {
      return {
        ...staff,
        inactive: staff.inactive || false,
      };
    });

    if (props.hideInactive === true) result = StaffUtils.filterActiveUsers(result);
    else result = PerformanceUtils.sortBy(result, ['inactive'], false);

    setResults(result);
    setSearching(false);
  }

  function onStaffClick() {
    if (clickedStaff) {
      let route = RouteUtils.getRoute(RouteUtils.routes().staff, {
        ':staffId': clickedStaff.id,
      });
      nav.push(route);
    }
  }

  let contentClass = 'search-field-content';
  if (clickedStaff) contentClass += ' has-staff';

  return (
    <div className="staff-search-field">
      <div className="search-field">
        {!props.hideTitle && <span className="small-title">Staff</span>}
        <div className={contentClass}>
          {!!clickedStaff && (
            <>
              <Icon
                src="xmark"
                alt="Clear Staff"
                showTooltip={true}
                onClick={() => setClickedStaff()}
                className="clear-staff"
              />
              <StaffBadge
                user={clickedStaff}
                onClick={!!props.ignoreClicks ? null : onStaffClick}
              />
            </>
          )}
          {!clickedStaff && (
            <DefaultReactSelect
              className="staff-search"
              name="search"
              placeholder="Staff Search"
              inputValue={search || ''}
              menuIsOpen={menuOpen}
              setMenuOpen={setMenuOpen}
              filterOption={() => true}
              loading={searching}
              noOptionsMessage="No staff found"
              onChange={(data) => {
                setClickedStaff(data.value);
              }}
              onInputChange={(input) => {
                handleSearchChange(input);
              }}
              options={results.map((staff) => {
                return {
                  value: staff,
                  label: StaffUtils.getName(staff),
                };
              })}
              components={{
                Option: ({ data, innerRef, isFocused }) => {
                  return (
                    <SelectableListItem
                      divider={true}
                      buttonRef={innerRef}
                      selected={isFocused}
                      special={isFocused}
                      key={data.value.id}
                      onClick={() => {
                        setClickedStaff(data.value);
                        setMenuOpen(false);
                      }}
                      item={<StaffBadge user={data.value} />}
                    />
                  );
                },
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

StaffSearchField.propTypes = {
  onSelect: PropTypes.func,
  staff: PropTypes.object,
  hideInactive: PropTypes.bool,
  ignoreAdmins: PropTypes.bool,
  ignoreClicks: PropTypes.bool,
  prefill: PropTypes.array,
};

export default StaffSearchField;
